import React from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

import { config } from '@config/pendo.config.json';
import { AIEvaluationStatus } from 'redux/schemas/models/submissions';
import { dunes, seafoam } from 'athena/styles/colors';
import { black, hexToRgbaString, lowTide } from 'styles/global_defaults/colors';
import { headerFontSize, headerLineHeight, headerRegularLineHeight, lightFontWeight, normalFontWeight, openSans, textExtraLargeFontSize, textMediumFontSize } from 'styles/global_defaults/fonts';
import {
  halfSpacing,
  largeSpacing,
  standardSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';

import { ReactComponent as HeroDotsLeft } from 'images/hero-dots-left.svg';
import { ReactComponent as HeroDotsRight } from 'images/hero-dots-right.svg';

type HeroVariant = {
  background: string;
  badgeBackground: string;
  emoji: string;
  title: string;
  withDots: boolean;
};

const getVariant = (evaluation: AIEvaluationStatus): HeroVariant => {
  switch (evaluation) {
    case AIEvaluationStatus.EXCEEDS_EXPECTATIONS:
      return {
        background: hexToRgbaString(seafoam, 0.3),
        badgeBackground: hexToRgbaString(seafoam, 0.6),
        emoji: '🏆',
        title: t.SUBMISSIONS.AI_ASSIGNMENT.RATING.AMAZING(),
        withDots: false,
      };
    case AIEvaluationStatus.NEEDS_IMPROVEMENT:
      return {
        background: hexToRgbaString(dunes, 0.3),
        badgeBackground: hexToRgbaString(dunes, 0.6),
        emoji: '🔧',
        title: t.SUBMISSIONS.AI_ASSIGNMENT.RATING.NEEDS_IMPROVEMENT(),
        withDots: true,
      };
    case AIEvaluationStatus.MEETS_EXPECTATIONS:
    default:
      return {
        background: hexToRgbaString(lowTide, 0.3),
        badgeBackground: hexToRgbaString(lowTide, 0.6),
        emoji: '👍',
        title: t.SUBMISSIONS.AI_ASSIGNMENT.RATING.GOOD(),
        withDots: true,
      };
  }
};

const styles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${largeSpacing}px 0;
  gap: ${halfSpacing}px;

  .hero-top {
    position: relative;
    width: 294px;
    height: ${tripleSpacing}px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .hero-dots {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  .hero-dots-left {
    left: 0;
  }

  .hero-dots-right {
    right: 0;
  }

  .hero-badge {
    width: ${tripleSpacing}px;
    height: ${tripleSpacing}px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${openSans};
    font-weight: ${normalFontWeight};
    font-size: ${headerFontSize}px;
    line-height: 1;
  }

  .hero-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    padding: 0 ${standardSpacing}px;
    text-align: center;
  }

  .hero-title {
    font-family: ${openSans};
    font-weight: ${lightFontWeight};
    font-size: ${textExtraLargeFontSize}px;
    line-height: ${headerRegularLineHeight}px;
    color: ${black};
    margin: 0;
  }

  .hero-reason {
    font-family: ${openSans};
    font-weight: ${normalFontWeight};
    font-size: ${textMediumFontSize}px;
    line-height: ${headerLineHeight}px;
    color: ${black};
    margin: 0;
  }
`;

type Props = {
  evaluation: AIEvaluationStatus;
  reason?: string;
};

const Hero: React.FC<Props> = ({ evaluation, reason }) => {
  const variant = getVariant(evaluation);
  const heroDataQa = config.pendo.submissionView.aiInsights.hero;
  const badgeDataQa = config.pendo.submissionView.aiInsights.heroBadge;

  return (
    <div
      css={[styles, css`background: ${variant.background};`]}
      data-qa={heroDataQa}
    >
      <div className='hero-top'>
        {variant.withDots && (
          <>
            <HeroDotsLeft className='hero-dots hero-dots-left' width={109} height={25} aria-hidden />
            <HeroDotsRight className='hero-dots hero-dots-right' width={109} height={25} aria-hidden />
          </>
        )}
        <div
          className='hero-badge'
          css={css`background: ${variant.badgeBackground};`}
          data-qa={badgeDataQa}
          role='img'
          aria-label={variant.title}
        >
          {variant.emoji}
        </div>
      </div>
      <div className='hero-text'>
        <h2 id='ai-insights-title' className='hero-title'>{variant.title}</h2>
        {reason && <p className='hero-reason'>{reason}</p>}
      </div>
    </div>
  );
};

export default Hero;
